/**
 * RosterAgent — covers two related flows that previously lived inline in
 * `CoachSteve.tsx`:
 *  - **addPlayer**: create a player + player card from natural language.
 *  - **setInjuryStatus**: mark a player as healthy/questionable/doubtful/out.
 *
 * Both flows are multi-turn (we may need to ask for missing info), so the
 * handlers cooperate with `pendingPlayerCreate` / `pendingInjuryUpdate`
 * state managed by `CoachSteve.tsx`.
 */

import { supabase } from '../../../lib/supabase';
import { playerCardService } from '../../../services/playerCardService';
import { InjuryService } from '../../../services/injuryService';
import { getSportPositionGroups } from '../../../utils/sportUtils';
import { isCreatePlayerIntent, isSetInjuryIntent } from './intents';
import {
  extractIsSub,
  extractJerseyNumber,
  extractPlayerName,
  extractInjuryStatusFromText,
} from './parsers';
import {
  extractPlayerFromInjuryMessage,
  fetchRosterForInjury,
  findPlayersForInjury,
} from './playerLookup';
import type { PlayerIdentifier } from './playerLookup';
import {
  extractPositionGroupsFromText,
  inferRoleTypesFromGroups,
} from './positionGroups';
import type {
  AgentRunContext,
  AgentRunResult,
  CoachSteveAgent,
  PendingPlayerCreate,
  PlayerBatchResult,
  QueuedPlayer,
} from './types';
import type { InjuryStatus } from '../../../constants/injuries';

/** Insert one player + player card. No messaging — callers decide how to report the outcome. */
async function createPlayerRecord(
  ctx: AgentRunContext,
  args: { firstName: string; lastName: string; number?: number; isSub: boolean; groups: string[] }
): Promise<{ success: true; playerId: string } | { success: false; reason: string }> {
  const { firstName, lastName, number, isSub, groups } = args;
  const sport = ctx.team.sport;
  const roleTypes = inferRoleTypesFromGroups(sport, groups);
  const { data: newPlayer, error: insertError } = await supabase
    .from('players')
    .insert({
      team_id: ctx.team.id,
      first_name: firstName,
      last_name: lastName,
      number: number ?? null,
      position: groups[0] || '--',
      experience: 'Beginner',
      injury_status: 'H',
      is_sub: isSub,
    })
    .select('id')
    .single();

  if (insertError || !newPlayer?.id) {
    console.error('Error creating player:', insertError);
    return { success: false, reason: "I couldn't create that player." };
  }

  const card = await playerCardService.getOrCreatePlayerCard(newPlayer.id, ctx.team.id);
  await playerCardService.updatePlayerCard(card.id, {
    eligible_positions: groups,
    role_types: roleTypes,
  });
  return { success: true, playerId: newPlayer.id };
}

/**
 * Format the final message once a player (or a whole multi-player batch) has
 * been fully processed. A single successful add keeps the original
 * one-player message + deep link; a batch lists every name instead (the
 * chat message type only supports one link, so batches skip it).
 */
function formatBatchSummary(
  batch: PlayerBatchResult,
  groups: string[]
): { message: string; link?: { label: string; href: string } } {
  const parts: string[] = [];
  let link: { label: string; href: string } | undefined;

  if (batch.added.length === 1 && batch.failed.length === 0) {
    const { name, playerId, isSub } = batch.added[0];
    parts.push(
      `Done — I added **${name}** to the roster${isSub ? ' as a **sub**' : ''}.\nPosition groups: ${groups.join(', ')}.`
    );
    link = { label: `Open ${name}`, href: `/player-cards/${playerId}` };
  } else if (batch.added.length > 0) {
    parts.push(
      `Done — I added ${batch.added.map((p) => `**${p.name}**`).join(', ')} to the roster.\nPosition groups: ${groups.join(', ')}.`
    );
  }

  if (batch.failed.length > 0) {
    parts.push(
      `I couldn't add ${batch.failed.map((f) => `**${f.name}**`).join(', ')}. Try adding ${batch.failed.length > 1 ? 'them' : 'that player'} individually from the Roster page.`
    );
  }

  if (parts.length === 0) {
    return { message: "I didn't add anyone — try again or add players from the Roster page." };
  }
  return { message: parts.join('\n\n'), link };
}

/**
 * Shared engine for both single- and multi-player "add player" requests.
 * Resolves `current`'s name, then its (shared) position group(s), inserting
 * as soon as both are known and moving on to the next queued player — so a
 * batch add asks for missing info one player at a time rather than all at
 * once or rejecting the whole list.
 */
async function processPlayerQueue(
  ctx: AgentRunContext,
  current: QueuedPlayer,
  groups: string[],
  queue: QueuedPlayer[],
  batch: PlayerBatchResult
): Promise<AgentRunResult> {
  const { firstName, lastName } = current;
  const inBatch = queue.length > 0 || batch.added.length > 0 || batch.failed.length > 0;

  if (!firstName || !lastName) {
    ctx.addMessage(
      'assistant',
      inBatch
        ? "What's the next player's first and last name?"
        : "Who should I add? Tell me the player's first and last name (and optionally their position groups)."
    );
    ctx.setPendingPlayerCreate({ firstName, lastName, number: current.number, isSub: current.isSub, groups, queue, batch });
    return { kind: 'needs_input' };
  }

  if (groups.length === 0) {
    const options = getSportPositionGroups(ctx.team.sport);
    const andMore = queue.length > 0 ? ` and ${queue.length} more` : '';
    ctx.addMessage(
      'assistant',
      `Got it — I can add **${firstName} ${lastName}**${current.isSub ? ' as a **sub**' : ''}${andMore}. What position group(s) should I set?\nOptions: ${options.length ? options.join(', ') : 'none available for this sport'}.`
    );
    ctx.setPendingPlayerCreate({ firstName, lastName, number: current.number, isSub: current.isSub, groups, queue, batch });
    return { kind: 'needs_input' };
  }

  const isSub = current.isSub ?? false;
  const result = await createPlayerRecord(ctx, { firstName, lastName, number: current.number, isSub, groups });
  const name = `${firstName} ${lastName}`;
  const nextBatch: PlayerBatchResult = result.success
    ? { added: [...batch.added, { name, playerId: result.playerId, isSub }], failed: batch.failed }
    : { added: batch.added, failed: [...batch.failed, { name, reason: result.reason }] };

  if (queue.length > 0) {
    const [next, ...rest] = queue;
    return processPlayerQueue(ctx, next, groups, rest, nextBatch);
  }

  await ctx.refreshTeamContextForAgent();
  ctx.setPendingPlayerCreate(null);
  const { message, link } = formatBatchSummary(nextBatch, groups);
  ctx.addMessage('assistant', message, link);
  return { kind: 'handled' };
}

/**
 * Continue an in-flight "add player" follow-up where the user is filling
 * in a missing name or position group(s) — for a single player, or the
 * next unresolved one in a multi-player batch.
 */
export async function continuePendingPlayerCreate(
  text: string,
  pending: PendingPlayerCreate,
  ctx: AgentRunContext
): Promise<AgentRunResult> {
  ctx.setIsLoading(true);
  try {
    const sport = ctx.team.sport;
    const nameFromReply = extractPlayerName(text);
    const firstName = pending.firstName || nameFromReply.firstName;
    const lastName = pending.lastName || nameFromReply.lastName;
    // Once shared groups are resolved for a batch, don't let a later reply
    // (e.g. the next player's name) re-parse and clobber them.
    const groups = pending.groups && pending.groups.length > 0
      ? pending.groups
      : extractPositionGroupsFromText(text, sport);

    return await processPlayerQueue(
      ctx,
      { firstName, lastName, number: pending.number, isSub: pending.isSub },
      groups,
      pending.queue ?? [],
      pending.batch ?? { added: [], failed: [] }
    );
  } catch (err) {
    console.error(err);
    ctx.addMessage(
      'assistant',
      "Something went wrong adding that player. Try again, or add them from the Roster page."
    );
    return { kind: 'error', error: String(err) };
  } finally {
    ctx.setIsLoading(false);
  }
}

/**
 * Run the full injury-status flow. Handles both the no-pending case and
 * the two pending kinds (`need_player`, `need_status`).
 */
export async function runInjuryStatusFlow(
  text: string,
  ctx: AgentRunContext
): Promise<AgentRunResult> {
  ctx.setIsLoading(true);
  try {
    let status: InjuryStatus | undefined;
    let playerIdentifier: PlayerIdentifier = {};
    const pendingInjuryUpdate = ctx.pendingInjuryUpdate;

    if (pendingInjuryUpdate) {
      if (pendingInjuryUpdate.kind === 'need_player') {
        status = pendingInjuryUpdate.status;
        playerIdentifier = extractPlayerFromInjuryMessage(text);
        const hasId =
          playerIdentifier.firstName ||
          playerIdentifier.lastName ||
          playerIdentifier.name ||
          playerIdentifier.number != null;
        if (!hasId) {
          ctx.addMessage(
            'assistant',
            "Which player? Tell me their first name, last name, nickname, or jersey number (e.g. \"Mike\" or \"#5\")."
          );
          return { kind: 'needs_input' };
        }
      } else {
        const statusFromText = extractInjuryStatusFromText(text);
        if (!statusFromText) {
          ctx.addMessage(
            'assistant',
            `What injury status for **${pendingInjuryUpdate.playerName}**? Say **healthy**, **questionable**, **doubtful**, or **out**.`
          );
          return { kind: 'needs_input' };
        }
        await InjuryService.updatePlayerInjuryStatus(
          pendingInjuryUpdate.playerId,
          statusFromText,
          ctx.team.id
        );
        await ctx.refreshTeamContextForAgent();
        ctx.setPendingInjuryUpdate(null);
        const label =
          statusFromText === 'H'
            ? 'Healthy'
            : statusFromText === 'Q'
              ? 'Questionable'
              : statusFromText === 'D'
                ? 'Doubtful'
                : 'Out';
        ctx.addMessage(
          'assistant',
          `Done — I set **${pendingInjuryUpdate.playerName}** as **${label}**.`
        );
        return { kind: 'handled' };
      }
    } else {
      status = extractInjuryStatusFromText(text);
      playerIdentifier = extractPlayerFromInjuryMessage(text);
    }

    if (!status) {
      const hasPlayerIdentifier =
        playerIdentifier.firstName ||
        playerIdentifier.lastName ||
        playerIdentifier.name ||
        playerIdentifier.number != null;
      if (hasPlayerIdentifier) {
        const roster = await fetchRosterForInjury(ctx.team.id);
        const matches = findPlayersForInjury(roster, playerIdentifier);
        if (matches.length === 1) {
          ctx.setPendingInjuryUpdate({
            kind: 'need_status',
            playerId: matches[0].id,
            playerName: `${matches[0].first_name} ${matches[0].last_name}`.trim(),
          });
          ctx.addMessage(
            'assistant',
            `What injury status for **${matches[0].first_name} ${matches[0].last_name}**? Say **healthy**, **questionable**, **doubtful**, or **out**.`
          );
        } else if (matches.length > 1) {
          const list = matches
            .map((p) => `**${p.first_name} ${p.last_name}**${p.number != null ? ` #${p.number}` : ''}`)
            .join(', ');
          ctx.addMessage(
            'assistant',
            `I found more than one: ${list}. Which one did you mean? (You can say their number or full name, then the status — e.g. "mark #5 out".)`
          );
        } else {
          ctx.addMessage(
            'assistant',
            "I don't see that player on the roster. You can use first name, last name, nickname, or jersey number (e.g. \"mark Mike out\" or \"set #5 as questionable\")."
          );
        }
      } else {
        ctx.addMessage(
          'assistant',
          "What status should I set? Say **healthy**, **questionable**, **doubtful**, or **out**. You can also say both: e.g. \"mark John Doe out\"."
        );
      }
      return { kind: 'needs_input' };
    }

    const roster = await fetchRosterForInjury(ctx.team.id);
    const matches = findPlayersForInjury(roster, playerIdentifier);

    if (matches.length === 0) {
      ctx.addMessage(
        'assistant',
        "I don't see that player on the roster. You can use first name, last name, nickname, or jersey number (e.g. \"mark Mike out\" or \"set #5 as questionable\")."
      );
      ctx.setPendingInjuryUpdate(null);
      return { kind: 'handled' };
    }
    if (matches.length > 1) {
      const list = matches
        .map((p) => `**${p.first_name} ${p.last_name}**${p.number != null ? ` #${p.number}` : ''}`)
        .join(', ');
      ctx.addMessage(
        'assistant',
        `I found more than one: ${list}. Which one did you mean? (Say their number or full name, then the status — e.g. "mark #5 out".)`
      );
      ctx.setPendingInjuryUpdate(null);
      return { kind: 'needs_input' };
    }

    const player = matches[0];
    await InjuryService.updatePlayerInjuryStatus(player.id, status, ctx.team.id);
    await ctx.refreshTeamContextForAgent();
    ctx.setPendingInjuryUpdate(null);
    const label =
      status === 'H'
        ? 'Healthy'
        : status === 'Q'
          ? 'Questionable'
          : status === 'D'
            ? 'Doubtful'
            : 'Out';
    ctx.addMessage(
      'assistant',
      `Done — I set **${player.first_name} ${player.last_name}**${player.number != null ? ` #${player.number}` : ''} as **${label}**.`
    );
    return { kind: 'handled' };
  } catch (err) {
    console.error(err);
    ctx.addMessage(
      'assistant',
      "Something went wrong updating injury status. Try again or set it from the Roster / Player Card."
    );
    ctx.setPendingInjuryUpdate(null);
    return { kind: 'error', error: String(err) };
  } finally {
    ctx.setIsLoading(false);
  }
}

/** Initial entrypoint for "add a player" requests. */
export async function runAddPlayer(
  text: string,
  ctx: AgentRunContext,
  params?: Record<string, unknown>
): Promise<AgentRunResult> {
  const sport = ctx.team.sport;
  const groups = extractPositionGroupsFromText(text, sport);

  // The router returns `params.players` (a list) when the message names more
  // than one player; the regex-dispatch path never has `params` at all, so it
  // always takes the single-player branch below.
  const rawPlayers = Array.isArray(params?.players)
    ? (params.players as Array<Record<string, unknown>>)
    : null;

  try {
    if (rawPlayers && rawPlayers.length > 1) {
      const queue: QueuedPlayer[] = rawPlayers.map((p) => ({
        firstName: p.firstName as string | undefined,
        lastName: p.lastName as string | undefined,
        number: p.number as number | undefined,
        isSub: p.isSub as boolean | undefined,
      }));
      const [first, ...rest] = queue;
      return await processPlayerQueue(ctx, first, groups, rest, { added: [], failed: [] });
    }

    // Single-player path (unchanged) — prefer LLM-extracted params from the
    // router, falling back to regex when params are not available.
    const parsedName = extractPlayerName(text);
    const firstName = (params?.firstName as string | undefined) || parsedName.firstName;
    const lastName = (params?.lastName as string | undefined) || parsedName.lastName;
    const number = (params?.number as number | undefined) ?? extractJerseyNumber(text);
    const isSub = (params?.isSub as boolean | undefined) ?? extractIsSub(text);

    return await processPlayerQueue(ctx, { firstName, lastName, number, isSub }, groups, [], { added: [], failed: [] });
  } catch (err) {
    console.error(err);
    ctx.addMessage(
      'assistant',
      "Something went wrong adding that player. Try again, or add them from the Roster page."
    );
    return { kind: 'error', error: String(err) };
  } finally {
    ctx.setIsLoading(false);
  }
}

/** Roster agent — exposes `addPlayer` via match/handle. Injury status is dispatched separately by the chat handler. */
export const rosterAgent: CoachSteveAgent = {
  name: 'roster',
  description:
    "Adds a player to the roster (creating their player card) and updates injury status (Healthy / Questionable / Doubtful / Out).",
  applicableSports: 'all',
  // Roster.match returns true for the "add player" path. Injury detection
  // is checked separately in the dispatcher because it shares the same
  // domain but has its own pending-flow continuations.
  match: (text) => isCreatePlayerIntent(text),
  async handle(text, ctx): Promise<AgentRunResult> {
    return runAddPlayer(text, ctx);
  },
};

/** Standalone injury intent matcher re-exported for the dispatcher. */
export function matchSetInjury(text: string): boolean {
  return isSetInjuryIntent(text);
}
